import dynamic from 'next/dynamic';

import { toForumLink } from 'lib/links';
import { isNumber } from 'lib/number';
import { toSidebarFeaturedOffers } from 'lib/offer';
import { resolvify } from 'lib/promises';
import { isParamsValid } from 'lib/request';

import { getAds } from 'services/ads';
import { getCategoriesList } from 'services/category';
import { getLandingsList } from 'services/landing';
import { getFeaturedOffersList } from 'services/offer';
import { getSuggestedSearchKeywordsList } from 'services/search';
import { getStoresList } from 'services/store';
import {
  getLastThreadsCategoryList,
  getThreadCategoriesList,
  getThreadInfo,
  getThreadsList,
} from 'services/thread';

import APP from 'constants/app';
import FORUM from 'constants/forum';
import LINKS from 'constants/links';
import STATUS_CODES from 'constants/statusCodes';

const ForumPage = dynamic(() => import('components/pages/Forum'));
const ThreadPage = dynamic(() => import('components/pages/Thread'));

const ForumThread = ({ isForumCategoryRoute, ...rest }) =>
  isForumCategoryRoute ? <ForumPage {...rest} /> : <ThreadPage {...rest} />;

export const getServerSideProps = async ({
  params: { threadOrCategoryParams },
}) => {
  if (!isParamsValid(threadOrCategoryParams)) {
    return { redirect: { destination: LINKS[404], permanent: true } };
  }

  const [threadOrCategorySlug, pageWord, page] = threadOrCategoryParams;

  const currPage = Number(page);

  if (
    (pageWord && pageWord !== FORUM.THREADS_WORD_PARAM) ||
    (!!page && (!isNumber(currPage) || currPage < 0))
  ) {
    return { redirect: { destination: LINKS[404], permanent: true } };
  }

  // Some of the old thread categories slugs had a corresponding
  // category after the last re-categorization, these are redirected
  // to their new corresponding slug
  if (
    FORUM.FROM_LEGACY_TO_NEW_THREAD_CATEGORIES_SLUGS.some(
      ({ legacySlug, slug }) =>
        legacySlug === threadOrCategorySlug && threadOrCategorySlug !== slug
    )
  ) {
    return {
      redirect: {
        destination: toForumLink({
          categorySlug: FORUM.FROM_LEGACY_TO_NEW_THREAD_CATEGORIES_SLUGS.find(
            ({ legacySlug }) => legacySlug === threadOrCategorySlug
          ).slug,
          page: currPage,
        }),
        permanent: false,
      },
    };
  }

  const [
    threadsCategories,
    categories,
    stores,
    featuredLandings,
    highlightedLandings,
    {
      data: {
        topAd: [topbarAd = null],
        topbarHighlight: navbarAds,
      },
    },
  ] = await resolvify([
    getThreadCategoriesList(),
    {
      fn: getCategoriesList({ selectedCategories: true }),
      fallback: getCategoriesList.fallback,
    },
    {
      fn: getStoresList({ from: 'topbar' }),
      fallback: getStoresList.fallback,
    },
    {
      fn: getLandingsList({ featured: true }),
      fallback: getLandingsList.fallback,
    },
    {
      fn: getLandingsList({ highlighted: true }),
      fallback: getLandingsList.fallback,
    },
    {
      fn: getAds(),
      fallback: getAds.fallback,
    },
  ]);

  // 'Recent' is used as the main listing and has a corresponding slug
  // `RECENT_THREADS_METADATA.categorySlug` on pagination, this was made
  // as a legacy compat layer.
  const isForumCategoryRoute =
    threadsCategories.some(
      ({ categorySlug }) => categorySlug === threadOrCategorySlug
    ) || threadOrCategorySlug === FORUM.RECENT_THREADS_METADATA.categorySlug;

  if (isForumCategoryRoute) {
    const threadsPage = currPage || 1;

    try {
      const fetchThreads = () =>
        threadOrCategorySlug &&
        threadOrCategorySlug !== FORUM.RECENT_THREADS_METADATA.categorySlug
          ? getLastThreadsCategoryList({
              limit: FORUM.THREADS_LIMIT,
              page: threadsPage,
              threadSlug: threadOrCategorySlug,
            })
          : getThreadsList({
              limit: FORUM.THREADS_LIMIT,
              page: threadsPage,
            });

      const serverThreads = await fetchThreads();
      const { total } = serverThreads;

      if (threadsPage > total && total !== 0) {
        return {
          redirect: {
            destination: toForumLink({ categorySlug: threadOrCategorySlug }),
            permanent: false,
          },
        };
      }

      return {
        props: {
          category:
            threadsCategories.find(
              ({ categorySlug }) => categorySlug === threadOrCategorySlug
            ) || FORUM.RECENT_THREADS_METADATA,
          gaEventCategory:
            threadOrCategorySlug === FORUM.RECENT_THREADS_METADATA.categorySlug
              ? FORUM.HOME_PAGE_GA_EVENT_CATEGORY
              : FORUM.CATEGORIES_PAGES_GA_EVENT_CATEGORY,
          isForumCategoryRoute,
          serverThreads,
          threadsCategories,
          threadsPage,
          [APP.CTX_OPTIONAL_PROPS]: {
            categories,
            featuredLandings,
            highlightedLandings,
            navbarAds,
            stores,
            topbarAd,
          },
        },
      };
    } catch ({ message }) {
      if (message === STATUS_CODES[401]) {
        return { props: { threadSlug: threadOrCategorySlug } };
      }

      if (message === STATUS_CODES[403]) {
        return { redirect: { destination: LINKS.HOME, permanent: true } };
      }

      return { redirect: { destination: LINKS[404], permanent: true } };
    }
  }

  try {
    const [{ recommendedOffers }, thread, featuredOffers] = await resolvify([
      getSuggestedSearchKeywordsList({ withOffers: true }),
      getThreadInfo({ threadSlug: threadOrCategorySlug }),
      { fn: getFeaturedOffersList(), fallback: getFeaturedOffersList.fallback },
    ]).catch(({ message }) => {
      throw new Error(message);
    });

    const sidebarFeaturedOffers = toSidebarFeaturedOffers(
      recommendedOffers,
      featuredOffers
    );

    return {
      props: {
        sidebarFeaturedOffers,
        thread,
        [APP.CTX_OPTIONAL_PROPS]: {
          categories,
          featuredLandings,
          highlightedLandings,
          navbarAds,
          stores,
          topbarAd,
        },
      },
    };
  } catch ({ message }) {
    if (message === STATUS_CODES[401]) {
      return { props: { threadSlug: threadOrCategorySlug } };
    }

    if (message === STATUS_CODES[403]) {
      return { redirect: { destination: LINKS.HOME, permanent: true } };
    }

    return { redirect: { destination: LINKS[404], permanent: true } };
  }
};

export default ForumThread;
